import React, {
  useContext,
  useState,
  FormEvent,
  useEffect,
  useRef,
} from 'react';
import { addRecentSearch } from 'src/areas/search-suggestions/history-storage';
import { AppContext } from '@prisjakt/ui-app-context';
import { BodyMediumText } from '@prisjakt/ui/text/variants';
import { Row } from '@prisjakt/ui/list/variants';

import {
  StyledSearchBar,
  InputWrapper,
  SearchInput,
  ClearIcon,
  ClearButton,
  SearchIcon,
  SearchButton,
  SearchButtonText,
  Text,
  SearchBarWrapper,
  SearchBarGroup,
  CloseButton,
} from './styles';
import { useHistory, useLocation } from 'react-router-dom';
import Suggestions from 'src/areas/search-suggestions';
import { IconChevronLeft } from '@prisjakt/ui/icon';
import { useEventListener } from 'src/hooks/use-event-listener';
import NoScroll from '../no-scroll';
import { useSearchBarContext } from './search-bar-context';
import { connect } from 'react-redux';
import searchActions from '../../areas/search/actions/search';
import { trackSearchCleared } from '../../areas/search/tracking';

type SearchBarProps = {
  query?: string;
  focused?: boolean;
  selected?: number;
  disabled?: boolean;
  visible?: boolean;
};

type DispatchProps = {
  searchFocus: () => void;
  searchBlur: () => void;
};

const SearchBar = (props: SearchBarProps & DispatchProps) => {
  const { value, setValue, active, setActive } = useSearchBarContext();
  const {
    query = '',
    focused = false,
    selected,
    disabled = false,
    visible = false,
    searchFocus,
    searchBlur,
  } = props;
  const { t } = useContext(AppContext);
  const searchBarRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const location = useLocation();

  const [currentQuery, setCurrentQuery] = useState(query);
  const [isFocused, setIsFocused] = useState(focused);
  const [isActive, setIsActive] = useState(false);

  const searchCriteria = currentQuery.length >= 2;

  const handleChange = (e: FormEvent<HTMLInputElement>) => {
    const newValue = (e.target as HTMLInputElement).value;
    setCurrentQuery(newValue);

    if (searchCriteria && !active) {
      setActive(true);
    }
  };

  useEffect(() => {
    if (!location.pathname.includes('/search')) {
      setCurrentQuery('');
      return;
    }

    const url = location.search;
    const searchParam = new URLSearchParams(url).get('search') || '';
    setCurrentQuery(searchParam);
  }, [location]);

  useEffect(() => {
    if (!searchBarRef.current || !visible) {
      return;
    }

    if (focused) {
      searchBarRef.current.focus();
      return;
    }
    searchBarRef.current.blur();
  }, [focused, searchBarRef]);

  useEffect(() => {
    if (value !== currentQuery) {
      setCurrentQuery(value);
    }
  }, [value]);

  useEffect(() => {
    if (active !== isActive) {
      setIsActive(active);
    }
  }, [active]);

  useEffect(() => {
    if (!visible) {
      setValue(currentQuery);
    }
  }, [visible]);

  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();

    if (disabled || !searchCriteria) {
      return;
    }
    addRecentSearch(currentQuery);
    setIsFocused(false);
    setActive(false);
    history.push(`/search?search=${encodeURIComponent(currentQuery)}`);
  };

  const handleClearSearchClick = () => {
    setCurrentQuery('');
    focusSearchBar();
    trackSearchCleared();
  };

  const setNewFocusedValue = (newFocusValue: boolean) => {
    if (!visible) {
      return;
    }

    if (newFocusValue && isFocused) {
      return;
    }

    if (newFocusValue) {
      searchFocus();
    } else {
      searchBlur();
    }

    setIsFocused(newFocusValue);
  };

  const focusSearchBar = () => {
    if (!searchBarRef.current) {
      return;
    }

    searchBarRef.current.focus();
    setActive(true);
  };

  useEventListener('focus-search-event', (e: CustomEvent) => {
    if (!e.detail.focus || !visible) {
      return;
    }

    focusSearchBar();
  });

  useEventListener('keyup', (e: KeyboardEvent) => {
    const key = e.key || e.keyCode;
    if (key === 'Escape' || key === 'Esc' || key === 27) {
      setNewFocusedValue(false);
      setActive(false);
    }
  });

  if (!visible) {
    return null;
  }

  return (
    <>
      <SearchBarWrapper $focused={isActive} $visible={visible}>
        <SearchBarGroup $active={isActive}>
          <CloseButton
            $visible={isActive}
            onClick={() => {
              setNewFocusedValue(false);
              setActive(false);
            }}
          >
            <IconChevronLeft size={30} />
          </CloseButton>
          <StyledSearchBar $active={isActive}>
            <InputWrapper>
              <BodyMediumText>
                <form action="/search" onSubmit={handleSubmit} role="search">
                  <SearchInput
                    ref={searchBarRef}
                    value={currentQuery}
                    data-suggestions="search"
                    role="combobox"
                    aria-expanded={isFocused && active}
                    aria-owns={
                      isFocused || active ? 'overlaySearchResults' : undefined
                    }
                    aria-autocomplete="both"
                    aria-activedescendant={
                      selected ? `selection${selected}` : ''
                    }
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    type="search"
                    placeholder={t('search', 'search.placeholder')}
                    onChange={handleChange}
                    maxLength={256}
                    onFocus={() => setNewFocusedValue(true)}
                    onBlur={() => setNewFocusedValue(false)}
                    onClick={() => setActive(true)}
                  />
                </form>
              </BodyMediumText>

              <ClearButton
                show={currentQuery.length > 0}
                data-test="ClearSearchButton"
                data-testid="clear-search-button"
                onClick={() => handleClearSearchClick()}
              >
                <ClearIcon size={12} />
              </ClearButton>
            </InputWrapper>

            <SearchButton
              disabled={!searchCriteria}
              active={isActive && visible}
              onClick={handleSubmit}
              data-test="SearchButton"
              type="button"
              aria-label={t('header', 'search.button')}
            >
              <SearchButtonText>
                <Row gap={0.5}>
                  <SearchIcon size={20} />
                  <Text $visible={searchCriteria}>
                    {t('header', 'search.button')}
                  </Text>
                </Row>
              </SearchButtonText>
            </SearchButton>
          </StyledSearchBar>
        </SearchBarGroup>

        <Suggestions
          visible={isActive && visible}
          query={currentQuery}
          setQuery={newQuery => setCurrentQuery(newQuery)}
          onClose={() => {
            setActive(false);
            setIsFocused(false);
          }}
          updatedInputElement={null}
          updatedSearchWrapperElement={null}
        />
      </SearchBarWrapper>
      <NoScroll mobile enabled={active && visible} />
    </>
  );
};

const dispatchActions = {
  searchFocus: searchActions.searchFocus,
  searchBlur: searchActions.searchBlur,
};

export default connect(null, dispatchActions)(SearchBar);
